import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const RoleSelect = ({
  label = 'Assigned Roles',
  placeholder = 'Select roles',
  searchPlaceholder = 'Search roles...',
  roles = [],
  selectedRoles = [],
  onToggle,
  isLoading = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const roleItems = useMemo(
    () => (Array.isArray(roles) ? roles : []),
    [roles],
  );

  const filteredRoles = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return roleItems;
    return roleItems.filter((role) => {
      const labelText = (role.name || role.role || '').toLowerCase();
      return labelText.includes(keyword);
    });
  }, [query, roleItems]);

  const handleToggle = (role) => {
    if (disabled || !onToggle) return;
    onToggle(role);
  };

  return (
    <div className="space-y-2">
      {label && <Label className="text-sm font-medium">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between"
            role="combobox"
            disabled={disabled}
          >
            <span className="truncate">{placeholder}</span>
            <div className="flex items-center gap-2 text-muted-foreground">
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              <ChevronDown className="h-4 w-4 shrink-0" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[320px] p-0 z-[1400]"
          align="start"
          sideOffset={8}
          forceMount
        >
          <div className="p-3 space-y-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-9"
            />
            <div className="max-h-64 overflow-y-auto rounded-md border">
              {filteredRoles.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">
                  No role found.
                </div>
              ) : (
                filteredRoles.map((role) => {
                  const checked = selectedRoles.includes(role.role);
                  return (
                    <button
                      key={role.role}
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-colors"
                      onClick={() => handleToggle(role)}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => handleToggle(role)}
                        aria-label={role.name || role.role}
                      />
                      <span className="truncate">{role.name || role.role}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

RoleSelect.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  roles: PropTypes.array,
  selectedRoles: PropTypes.array,
  onToggle: PropTypes.func,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default RoleSelect;


